/**
 * Servicio para evaluar la calidad pedagógica de las respuestas de Galia
 * Verifica si las respuestas del asistente son acordes a los objetivos de la lección
 */

import { LessonObjective } from './academicService';
import { postAiCompletion } from './aiCompletionClient';

export interface AssistantResponseQuality {
  is_appropriate: boolean; // Si la respuesta es apropiada para los objetivos
  encourages_objectives: boolean; // Si la respuesta motiva al estudiante a usar los objetivos
  provides_examples: boolean; // Si da ejemplos útiles
  corrects_appropriately: boolean; // Si corrige de forma pedagógica
  quality_score: number; // 0.0 a 1.0
  feedback: string; // Comentario breve sobre la calidad
  suggestions?: string[]; // Sugerencias de mejora (opcional)
}

class AssistantResponseEvaluationService {
  /**
   * Evalúa si la respuesta de Galia es pedagógicamente apropiada
   * @param assistantMessage Respuesta de Galia
   * @param userMessage Mensaje previo del usuario
   * @param objectives Objetivos de la lección actual
   * @returns Evaluación de calidad
   */
  async evaluateAssistantResponse(
    assistantMessage: string,
    userMessage: string,
    objectives: LessonObjective[]
  ): Promise<AssistantResponseQuality | null> {
    try {
      // Construir lista de objetivos
      const objectivesList = objectives.map(obj => {
        const type = obj.type || 'general';
        return `- [${type}] ${obj.description}`;
      }).join('\n');

      const systemPrompt = `Eres un evaluador pedagógico de respuestas de asistentes de IA para aprendizaje de francés.

CONTEXTO:
- El estudiante está en una lección con objetivos específicos de aprendizaje
- Galia (el asistente) debe ayudar al estudiante a alcanzar estos objetivos
- La interacción debe ser natural pero pedagógicamente efectiva

OBJETIVOS DE LA LECCIÓN ACTUAL:
${objectivesList}

TU TAREA:
Evaluar si la respuesta de Galia es APROPIADA y EFECTIVA considerando:

1. **is_appropriate**: ¿La respuesta es relevante y apropiada al contexto?
   - Responde al mensaje del estudiante de forma coherente
   - No introduce temas irrelevantes
   - Mantiene el nivel adecuado de formalidad/informalidad

2. **encourages_objectives**: ¿La respuesta motiva al estudiante a usar los objetivos?
   - Crea oportunidades para que el estudiante practique los objetivos
   - Hace preguntas que requieren usar las estructuras objetivo
   - Introduce vocabulario o temas relacionados con los objetivos

3. **provides_examples**: ¿Da ejemplos útiles cuando es apropiado?
   - Modela el uso correcto de estructuras gramaticales
   - Usa vocabulario objetivo de forma natural
   - Demuestra funciones comunicativas

4. **corrects_appropriately**: ¿Corrige errores de forma pedagógica?
   - Si el estudiante cometió un error relevante a los objetivos, ¿lo corrige?
   - ¿La corrección es clara pero no desalentadora?
   - ¿Ofrece la forma correcta de manera constructiva?

5. **quality_score**: Puntuación general de 0.0 a 1.0
   - 0.9-1.0: Excelente respuesta pedagógica
   - 0.7-0.8: Buena respuesta, efectiva
   - 0.5-0.6: Aceptable pero mejorable
   - 0.0-0.4: Respuesta inadecuada o poco efectiva

6. **feedback**: Comentario conciso (1-2 oraciones) sobre la calidad

RESPONDE CON JSON:
{
  "is_appropriate": true/false,
  "encourages_objectives": true/false,
  "provides_examples": true/false,
  "corrects_appropriately": true/false,
  "quality_score": 0.0-1.0,
  "feedback": "comentario breve",
  "suggestions": ["sugerencia 1", "sugerencia 2"] // opcional, solo si quality_score < 0.7
}`;

      const userPrompt = `MENSAJE DEL ESTUDIANTE:
"${userMessage}"

RESPUESTA DE GALIA:
"${assistantMessage}"

Evalúa la calidad pedagógica de la respuesta de Galia.`;

      const response = await postAiCompletion({
        model: 'gpt-4o-mini',
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt }
        ],
        temperature: 0.4,
        response_format: { type: 'json_object' }
      });

      if (!response.ok) {
        console.error('[AssistantEvaluation] Error en OpenAI API:', response.status);
        return null;
      }

      const data = await response.json();
      const content = data.choices[0]?.message?.content;

      if (!content) {
        console.error('[AssistantEvaluation] No se recibió contenido');
        return null;
      }

      const evaluation: AssistantResponseQuality = JSON.parse(content);

      // Validar estructura
      if (
        typeof evaluation.is_appropriate !== 'boolean' ||
        typeof evaluation.encourages_objectives !== 'boolean' ||
        typeof evaluation.provides_examples !== 'boolean' ||
        typeof evaluation.corrects_appropriately !== 'boolean' ||
        typeof evaluation.quality_score !== 'number' ||
        typeof evaluation.feedback !== 'string'
      ) {
        console.error('[AssistantEvaluation] Respuesta con estructura inválida');
        return null;
      }

      // Normalizar score
      evaluation.quality_score = Math.max(0, Math.min(1, evaluation.quality_score));

      // Log para debugging (solo en desarrollo)
      console.log('[AssistantEvaluation] Evaluación:', {
        quality_score: evaluation.quality_score,
        encourages_objectives: evaluation.encourages_objectives,
        feedback: evaluation.feedback
      });

      return evaluation;

    } catch (error) {
      console.error('[AssistantEvaluation] Error al evaluar respuesta:', error);
      return null;
    }
  }

  /**
   * Determina si una respuesta requiere atención/revisión
   * Útil para alertar a desarrolladores sobre respuestas de baja calidad
   */
  needsReview(evaluation: AssistantResponseQuality): boolean {
    return (
      evaluation.quality_score < 0.6 ||
      !evaluation.is_appropriate ||
      (!evaluation.encourages_objectives && !evaluation.provides_examples)
    );
  }

  /**
   * Genera reporte de calidad para logging/analytics
   */
  generateQualityReport(evaluations: AssistantResponseQuality[]): {
    average_quality: number;
    appropriate_percentage: number;
    encourages_percentage: number;
    needs_review_count: number;
    total_evaluated: number;
  } {
    if (evaluations.length === 0) {
      return {
        average_quality: 0,
        appropriate_percentage: 0,
        encourages_percentage: 0,
        needs_review_count: 0,
        total_evaluated: 0
      };
    }

    const avgQuality = evaluations.reduce((sum, e) => sum + e.quality_score, 0) / evaluations.length;
    const appropriate = evaluations.filter(e => e.is_appropriate).length;
    const encourages = evaluations.filter(e => e.encourages_objectives).length;
    const needsReview = evaluations.filter(e => this.needsReview(e)).length;

    return {
      average_quality: avgQuality,
      appropriate_percentage: (appropriate / evaluations.length) * 100,
      encourages_percentage: (encourages / evaluations.length) * 100,
      needs_review_count: needsReview,
      total_evaluated: evaluations.length
    };
  }
}

export const assistantResponseEvaluationService = new AssistantResponseEvaluationService();
export default assistantResponseEvaluationService;
